import React, { useEffect } from "react";

/**
 * Centered modal overlay. Hand-rolled (matches the Drawer/BottomSheet
 * pattern — no headless-ui dep). Closes on backdrop click or Escape.
 *
 * Body scroll is locked while open. The content area scrolls internally
 * so a long ticket history doesn't push the chrome off-screen.
 */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Header element — title + optional context badges. */
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Tailwind width utilities, default `max-w-2xl`. */
  widthClass?: string;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  widthClass = "max-w-2xl",
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`relative bg-white rounded-lg shadow-xl w-full ${widthClass} max-h-[85vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200">
          <div className="text-sm font-semibold text-secondary-800">
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-secondary-500 hover:text-secondary-800 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
};

export default Modal;
